import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React from 'react';

import { useI18n } from '../../../../../application/language/useI18n';
import { Colors } from '../../../../design/color/Colors';
import { IUnitCardModel } from '../../../../../service/unit/interface/IUnitCardModel';
import { OverflownContent } from '../../LearningUnitPreviewPage/shared/OverflownContent';
import { FilterValues } from '../PassingListPage';
import { IGroupedUnit } from '../store/IGroupedUnit';

interface UnitsListProps {
  title: string;
  units: IUnitCardModel[];
  scrollToId?: (id: string) => void;
}

const UnitsList = ({ title, units, scrollToId }: UnitsListProps) => {
  const classes = useStyles();
  if (!units.length) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          borderBottom: `0.5px solid ${Colors.border}`,
          paddingBottom: '8px',
          marginBottom: '8px',
        }}
      >
        <Box
          sx={{
            border: `1px solid ${Colors.border}`,
            borderRadius: '32px',
            alignSelf: 'self-start',
            padding: '0px 12px',
            marginRight: '8px',
          }}
        >
          {units.length}
        </Box>
        <Box sx={{ color: 'rgb(120, 120, 120)' }}>{title}</Box>
      </Box>
      <Box
        sx={{
          gap: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {units?.map((unit) => (
          <OverflownContent
            key={unit.id}
            sx={{ '&:hover': { color: 'rgb(120, 120, 120)' } }}
            contentClasses={classes.groupTitle}
            onClick={() => {
              if (scrollToId) {
                scrollToId(unit.id);
              } else {
                document.getElementById(unit.id)?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {unit.settings.titleHeading}
          </OverflownContent>
        ))}
      </Box>
    </Box>
  );
};

interface UserUnitListCardProps {
  group: IGroupedUnit;
  filter: FilterValues;
  scrollToId?: (id: string) => void;
}

export const UserUnitListCard = observer(({ group, filter, scrollToId }: UserUnitListCardProps) => {
  const i18n = useI18n();
  const classes = useStyles();
  const unitsTotal =
    filter === FilterValues.All
      ? group.importantUnitList.length + group.unimportantUnitList.length
      : filter === FilterValues.Important
      ? group.importantUnitList.length
      : group.unimportantUnitList.length;

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        background: Colors.bgSurface,
        '&.Mui-expanded': { margin: '0' },
        '&.MuiAccordion-rounded': { borderRadius: '8px', boxShadow: 'none' },
        '&.MuiAccordion-rounded::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '&.Mui-expanded': { borderBottom: `0.5px solid ${Colors.border}`, minHeight: 'auto' },
          '&.MuiAccordionSummary-content': { justifyContent: 'space-between', alignItems: 'center' },
        }}
      >
        <Box
          sx={{
            background: Colors.bgSurface,
            borderRadius: '32px',
            alignSelf: 'self-start',
            padding: '0px 12px',
            marginRight: '8px',
          }}
        >
          {unitsTotal}
        </Box>
        <OverflownContent contentClasses={classes.groupTitle}>{group.groupName}</OverflownContent>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            gap: '8px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {filter === FilterValues.All ? (
            <>
              <UnitsList units={group.importantUnitList} title={i18n.translate('phrases.mandatory')} scrollToId={scrollToId} />
              <UnitsList units={group.unimportantUnitList} title={i18n.translate('phrases.optional')} scrollToId={scrollToId} />
            </>
          ) : filter === FilterValues.Important ? (
            <UnitsList units={group.importantUnitList} title={i18n.translate('phrases.mandatory')} scrollToId={scrollToId} />
          ) : (
            <UnitsList units={group.unimportantUnitList} title={i18n.translate('phrases.optional')} scrollToId={scrollToId} />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

const useStyles = makeStyles(() => ({
  groupTitle: {
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    whiteSpace: 'normal',
    lineClamp: 1,
  },
}));
